/**
 * @fileoverview Solvency Analysis — Overview snapshot
 * @module jobs/phases/overview/investigations/SolvencySnapshot
 *
 * Purpose: Compact snapshot of the solvency indicators summary bar
 * ("N assessed | X met Y not met Z insufficient  Earliest: …") for the
 * Investigations tab. Counts and the earliest indication date are derived
 * client-side from the canonical indicator entries, exactly as the full
 * FinancialIndicatorsPanel summary bar does. "See details" opens the Solvency
 * Analysis view.
 *
 * Dependencies:
 * - Convex: summarization.publicQueries.listFinancialIndicatorEntriesForJob
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import {
  ChartColumnDecreasing,
  CheckCircle2,
  Minus,
  XCircle,
} from "lucide-react";
import { api } from "@cvx/_generated/api";
import type { Doc, Id } from "@cvx/_generated/dataModel";
import { cn } from "@/utils/misc";
import { InvestigationSnapshotCard } from "./InvestigationSnapshotCard";

interface SolvencySnapshotProps {
  jobId: Id<"jobs">;
  onSeeDetails: () => void;
}

/** Format an ISO date (YYYY-MM-DD) as "30 June 2023". */
function formatDate(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return `${day} ${months[month - 1]} ${year}`;
}

/**
 * Solvency indicators snapshot for the Overview → Investigations tab.
 *
 * @component
 */
export function SolvencySnapshot({
  jobId,
  onSeeDetails,
}: SolvencySnapshotProps) {
  const { data, isError } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listFinancialIndicatorEntriesForJob,
      { jobId },
    ),
  );
  // The query declares `returns: v.array(v.any())`; its handler returns
  // financialIndicatorEntries docs verbatim, typed here at the boundary.
  const indicators: Doc<"financialIndicatorEntries">[] = data ?? [];

  const metCount = indicators.filter((i) => i.assessment === "met").length;
  const notMetCount = indicators.filter(
    (i) => i.assessment === "not_met",
  ).length;
  const insufficientCount = indicators.filter(
    (i) => i.assessment === "insufficient_data",
  ).length;
  const total = indicators.length;

  // Earliest indication date among "met" indicators — mirrors the estimated
  // insolvency date the manifest derives from the same rows.
  const earliestDate = indicators
    .filter((i) => i.assessment === "met" && i.earliestIndicationDate)
    .map((i) => i.earliestIndicationDate)
    .filter((d): d is string => typeof d === "string")
    .sort((a, b) => a.localeCompare(b))[0];

  const signal = metCount >= 5 ? "red" : metCount >= 2 ? "amber" : "green";
  const signalStyles: Record<typeof signal, string> = {
    red: "border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20",
    amber:
      "border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/20",
    green:
      "border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/50 dark:bg-emerald-950/20",
  };

  // Hide the whole card when the Solvency agent isn't engaged on this job:
  // with no financial documents ingested there are no indicator rows, so an
  // empty placeholder adds no value (MOR-1254). Stays hidden while loading too
  // (no rows yet), and only surfaces once engaged — or on a genuine load error.
  if (!isError && total === 0) return null;

  return (
    <InvestigationSnapshotCard
      icon={ChartColumnDecreasing}
      title="Solvency Analysis"
      onSeeDetails={onSeeDetails}
    >
      {isError ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">
          Couldn't load solvency indicators.
        </p>
      ) : (
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3",
            signalStyles[signal],
          )}
        >
          <div className="flex items-center gap-3 text-xs">
            <span className="font-medium">{total} assessed</span>
            <span className="text-border">|</span>
            <span className="flex items-center gap-1 font-medium text-red-700 dark:text-red-400">
              <XCircle className="h-3 w-3" /> {metCount} met
            </span>
            <span className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
              <CheckCircle2 className="h-3 w-3" /> {notMetCount} not met
            </span>
            {insufficientCount > 0 && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <Minus className="h-3 w-3" /> {insufficientCount} insufficient
              </span>
            )}
          </div>
          {earliestDate && (
            <span className="text-xs">
              <span className="text-muted-foreground">Earliest: </span>
              <span className="font-semibold text-red-700 dark:text-red-400">
                {formatDate(earliestDate)}
              </span>
            </span>
          )}
        </div>
      )}
    </InvestigationSnapshotCard>
  );
}
